/**
 * "Grant this machine access to your encrypted work": shown wherever an enrolled machine cannot
 * read or send encrypted content yet (Add project, Now, You › Machines, right after enrollment).
 * The browser that approved the machine grants it automatically with a device-signed directory
 * update (no passkey tap); a passkey is needed only on a fresh account without a key directory
 * or while a revocation rotation is pending. The relay never supplies the machine's key (see
 * lib/machine-access.ts and docs/e2e.md "Fewer passkey ceremonies").
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Fingerprint, KeyRound } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import type { BrowserContentSession } from '../lib/content-session.js'
import {
  grantMachineAccess,
  grantMachineAccessSilently,
  type MachineAccess,
  machineAccessKey,
  waitForMachineIdentity,
} from '../lib/machine-access.js'
import { useMachines } from '../lib/queries.js'
import type { Machine } from '../lib/types.js'
import { Button, errorText } from './ui.js'

/** Access state for one machine; polled while vikod has yet to publish its content identity. */
export function useMachineAccess(machineId: string, online: boolean) {
  const b = useBackend()
  return useQuery<MachineAccess>({
    queryKey: machineAccessKey(machineId),
    enabled: !!b.content,
    queryFn: () => (b.content as BrowserContentSession).machineAccess(machineId),
    refetchInterval: (q) => (online && q.state.data === 'identity-pending' ? 3_000 : false),
    staleTime: 10_000,
  })
}

/**
 * The grant action; refreshes machine state and reports once granted. `silent` never opens a
 * passkey sheet: it resolves false when only a passkey ceremony could grant.
 */
export function useGrantMachineAccess(machineId: string) {
  const b = useBackend()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const grant = async (o: { silent?: boolean } = {}) => {
    if (!b.content) return false
    setBusy(true)
    setError(null)
    try {
      if (o.silent) {
        if (!(await grantMachineAccessSilently(b.content, machineId))) return false
      } else await grantMachineAccess(b.content, machineId)
      await qc.invalidateQueries({ queryKey: ['machines'] })
      return true
    } catch (e) {
      // A dismissed passkey sheet is not an error worth showing.
      if ((e as Error)?.name !== 'NotAllowedError') setError(errorText(e))
      return false
    } finally {
      setBusy(false)
    }
  }
  return { grant, busy, error }
}

/**
 * Inline notice for one machine. Renders nothing when the machine already has the key, the
 * account is not end-to-end encrypted, or this browser is not trusted for encrypted content.
 */
export function MachineAccessNotice({
  machine: m,
  compact,
}: {
  machine: Pick<Machine, 'id' | 'name' | 'online'>
  compact?: boolean
}) {
  const access = useMachineAccess(m.id, m.online)
  const { grant, busy, error } = useGrantMachineAccess(m.id)
  const state = access.data
  // Zero-tap grant for a machine this browser approved: try once, silently (never a sheet).
  const autoTried = useRef(false)
  const grantRef = useRef(grant)
  grantRef.current = grant
  useEffect(() => {
    if (state !== 'needs-grant' || autoTried.current) return
    autoTried.current = true
    void grantRef.current({ silent: true })
  }, [state])
  if (access.isError)
    return (
      <p role="alert" className="m-0 text-[14px] text-status-fail">
        Could not verify {m.name}’s encryption key: {errorText(access.error)}
      </p>
    )
  if (!state || state === 'granted' || state === 'locked') return null
  if (state === 'identity-pending')
    return (
      <p className="m-0 text-[14px] text-text-3" data-access="identity-pending">
        {m.online
          ? `Waiting for ${m.name} to share its encryption key. If this persists, update viko on it.`
          : `${m.name} hasn’t shared its encryption key yet. Start viko on it, then grant it access here.`}
      </p>
    )
  if (state === 'approve-elsewhere')
    return (
      <p className="m-0 text-[14px] text-text-3" data-access="approve-elsewhere">
        {m.name} needs access to your encrypted work. Grant it from the browser that approved this
        machine.
      </p>
    )
  return (
    <div
      className={`grid gap-2 rounded-lg bg-accent-soft ${compact ? 'px-3 py-2' : 'p-3'}`}
      data-access="needs-grant"
    >
      <div className="flex items-start gap-2">
        <KeyRound size={15} aria-hidden="true" className="mt-0.5 flex-none text-accent-text" />
        <div className="grid min-w-0 flex-1 gap-0.5">
          <span className="text-[14px] font-semibold">
            Grant {compact ? m.name : 'this machine'} access to your encrypted work
          </span>
          {compact ? null : (
            <span className="text-[14px] text-text-2">
              {m.name} is connected, but it can’t send its project list or reports, or read your
              encrypted projects, until this browser grants it your encryption key.
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="primary"
          icon={<Fingerprint size={14} />}
          disabled={busy}
          aria-label={`Grant ${m.name} access`}
          onClick={() => void grant()}
        >
          {busy ? 'Granting…' : 'Grant access'}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="m-0 text-[14px] text-status-fail">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Now: one compact notice per machine that is waiting for the key. */
export function MachineAccessBanner() {
  const b = useBackend()
  const machines = useMachines()
  if (!b.content || !machines.data?.length) return null
  return (
    <div className="grid gap-2 px-4 pb-2 empty:hidden">
      {machines.data.map((m) => (
        <NeedsGrantOnly key={m.id} machine={m} />
      ))}
    </div>
  )
}
function NeedsGrantOnly({ machine }: { machine: Machine }) {
  const access = useMachineAccess(machine.id, machine.online)
  return access.data === 'needs-grant' ? <MachineAccessNotice machine={machine} compact /> : null
}

/**
 * Right after enrollment approval: wait for vikod to publish its content identity, then grant
 * in the same flow with no further tap (device-signed; the approval tap already covered it).
 * Only when a passkey is still required (no key directory yet) does a sheet open, and then only
 * while the approval gesture is fresh; otherwise it is one explicit tap.
 */
export function EnrollmentAccessStep({
  machineId,
  name,
  approvedAt,
}: {
  machineId: string
  name: string
  approvedAt: number
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const access = useMachineAccess(machineId, true)
  const { grant, busy, error } = useGrantMachineAccess(machineId)
  const tried = useRef(false)
  const grantRef = useRef(grant)
  grantRef.current = grant
  useEffect(() => {
    if (!b.content || tried.current) return
    const ctl = new AbortController()
    // vikod publishes its identity once its service runs; the zero-tap grant can wait for it.
    void waitForMachineIdentity(b.api, machineId, { timeoutMs: 120_000, signal: ctl.signal })
      .then(async (ready) => {
        if (!ready || ctl.signal.aborted || tried.current) return
        await qc.invalidateQueries({ queryKey: machineAccessKey(machineId) })
        const state = await (b.content as BrowserContentSession).machineAccess(machineId)
        if (state !== 'needs-grant' || tried.current) return
        tried.current = true
        if (await grantRef.current({ silent: true })) return
        if (Date.now() - approvedAt < 10_000) await grantRef.current()
      })
      .catch(() => {})
    return () => ctl.abort()
  }, [b, machineId, approvedAt, qc])
  if (!b.content) return null
  if (access.data === 'granted')
    return (
      <p className="m-0 text-[14px] text-text-2" role="status">
        {name} can read and send your encrypted work.
      </p>
    )
  if (access.data === 'identity-pending')
    return (
      <p className="m-0 text-[14px] text-text-3" role="status">
        Once viko is running on {name}, grant it access to your encrypted work here or from Add
        project.
      </p>
    )
  if (access.data === 'needs-grant' && busy)
    return (
      <p className="m-0 text-[14px] text-text-2" role="status">
        Giving {name} access to your encrypted work…
      </p>
    )
  return (
    <>
      <MachineAccessNotice machine={{ id: machineId, name, online: true }} />
      {error ? (
        <p role="alert" className="m-0 text-[14px] text-status-fail">
          {error}
        </p>
      ) : null}
    </>
  )
}
